import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { finalize, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { get } from 'lodash';

import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { BaseModalComponent } from 'common/src/modules/rnpl-common/components';
import { BundledProduct, ProductModel, ProductsService, RelatedProductModel } from '../../../products';
import { TradeOfferModel, TradeOfferPositionsByProductType } from 'projects/workspace/src/app/trade-offer/models';
import { TradeOfferApiService } from 'projects/workspace/src/app/trade-offer/services/trade-offer-api.service';
import { ResponseModel } from 'projects/workspace/src/app/shared/models/response';
import { ProductTypes } from '../../../products/product-types';
import { CompositeProductModalComponent } from '../../modals-products';
import { CustomSearchFn, getProductSearchValue } from 'common/src/modules/rnpl-common/helpers';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { ActionButtonsService } from '../../../../services/action-buttons.service';
import { DocumentTypesUppercaseEnum } from '../../modals-common/link-document-modal/enums/ducument-types.enum';
import { RecalculatePositionApiService } from '../../../../services/recalculate-position-api.service';
import { PositionGroupModel, ProductUnitModel } from 'projects/workspace/src/app/shared/models';
import { ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { PositionGroupingEnum, PositionMediaTypeEnum, PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';
import { PositionMediaFormComponent } from 'projects/workspace/src/app/shared/forms/position-media-form/position-media-form.component';
import { PdfSettingsAlignEnum } from 'projects/workspace/src/app/pdf-settings/enums';
import { ARBITRARY_POSITION_TYPES_LIST } from 'projects/workspace/src/app/accounting/accounting.constants';

@Component({
  selector: 'rnpl-position-modal',
  templateUrl: './offer-position-modal.component.html',
  styleUrls: ['./offer-position-modal.component.scss']
})
export class OfferPositionModalComponent extends BaseModalComponent implements OnInit, OnDestroy {

  public bundledProducts: Partial<BundledProduct>[];
  public activeTab: 'OVERVIEW'|'BUNDLE_PRODUCTS' = 'OVERVIEW';
  public tabs = [
    {
      tabName: 'OVERVIEW',
      label: 'APP.POSITION'
    },
    {
      tabName: 'BUNDLE_PRODUCTS',
      label: 'BUNDLES.PRODUCTS_IN_BUNDLE'
    }
  ];

  public unitsList: string[] = [];
  public unitsListSource: ProductUnitModel[] = [];
  public companyProfile: CompanyProfile;
  public previousUnitType: string;
  public previousUpdatedPosition: any;
  public positionName: string = null;

  public productTypeRadio: 'common'|'general' = 'common';
  public currentStep: 'positionInfo'|'relatedProducts' = 'positionInfo';

  public readonly productTypes = ProductTypes;
  public positionMediaTypeEnum = PositionMediaTypeEnum;
  public positionGroupingEnum = PositionGroupingEnum;
  public arbitraryPositionTypesList = ARBITRARY_POSITION_TYPES_LIST;

  public selectedProductInvalid: boolean = false;
  public productNameInvalid: boolean = false;

  public productsList: ProductModel[] = [];
  public relatedProducts: RelatedProductModel[] = [];

  public selectedProduct: ProductModel = null;
  public selectedProductType: ProductTypes|string = ProductTypes.GOODS;

  public marginTotal: number = null;
  public marginPercent: number = null;

  public customSearchFn = CustomSearchFn;

  public form: FormGroup;

  @ViewChild('positionMediaFormComponent', { static: false }) positionMediaFormComponentRef: PositionMediaFormComponent;

  readonly generalProductsList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  readonly showDropdownSpin$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly addPositionLoadingRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);


  constructor(
    public store: Store<AppState>,
    public toasterService: ToasterService,
    public translateService: TranslateService,
    public productsService: ProductsService,
    public tradeOfferApiService: TradeOfferApiService,
    public dialogRef: MatDialogRef<OfferPositionModalComponent>,
    public dialog: MatDialog,
    private fb: FormBuilder,
    public actionButtonsService: ActionButtonsService,
    private recalculatePositionApiService: RecalculatePositionApiService,
    private productUnitApiService: ProductUnitApiService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      tradeOfferId: number;
      tradeOffer: TradeOfferModel;
      groupId: string;
      salesPriceListName: string;
      vatBlocked: boolean;
    }
  ) {
    super(toasterService);

    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => this.companyProfile = companyProfile);
  }

  ngOnInit() {
    this.initForm(this.selectedProduct);
    this.getProducts();
    // this.getGeneralProductsListDropdown();
    this.productTypeControl.setValue(ProductTypes.GOODS);

    this.dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.actionButtonsService.hideSidebarHandler());
  }

  // private getGeneralProductsListDropdown(): void {
  //   this.productsService.getGeneralProductsListDropdown(false)
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((response) => {
  //       const filteredGeneralProducts = response
  //         .filter(glProduct => glProduct.entityForm === 'services')
  //         .filter(glProduct => glProduct.isVisibleForList);
  //       this.generalProductsList$.next(filteredGeneralProducts);
  //     });
  // }

  private initForm(selectProduct: ProductModel = {} as ProductModel): void {
    this.form = this.fb.group({
      grossTotalPrice: [this.setCurrencyValue(get(selectProduct, 'prices.price.grossPrice')), Validators.required],
      grossUnitPrice: [this.setCurrencyValue(get(selectProduct, 'prices.price.grossPrice')), Validators.required],
      netTotalPrice: [this.setCurrencyValue(get(selectProduct, 'prices.price.netPrice')), Validators.required],
      productDescription: [get(selectProduct, 'description')],
      providedServices: [get(selectProduct, 'description')],
      netUnitPrice: [this.setCurrencyValue(get(selectProduct, 'prices.price.netPrice')), Validators.required],
      vat: [
        get(selectProduct, 'prices.price.vat') || get(selectProduct, 'prices.price.vat') === 0
          ? get(selectProduct, 'prices.price.vat')
          : get(selectProduct, 'accountingSettings.vatRateByDefault')
      ],
      discount: [get(selectProduct, 'prices.price.discount')],
      discountAmountNet: [this.setCurrencyValue(get(selectProduct, 'prices.price.discountNet'))],
      discountAmountGross: [this.setCurrencyValue(get(selectProduct, 'prices.price.discountGross'))],
      addDiscount: [!!get(selectProduct, 'prices.price.discount')],
      quantity: [1],
      addRemark: [false],
      remark: [],
      productType: [],
      arbitraryProductType: [ProductTypes.GOODS],
      unitType: [get(selectProduct, 'unitType') || 'pcs'],
    });
  }

  selectPositionChanges(selectedProduct: ProductModel): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.REGULAR);
    this.selectedProductType = selectedProduct.type || selectedProduct.productType;
    if (this.productTypeRadio === 'general') {
      this.selectedProductType = ProductTypes.GENERAL;
    }

    if (this.data.vatBlocked) {
      this.vat.disable({onlySelf: true, emitEvent: false});
      if (this.priceOptionGross) {
        this.netUnitPrice.disable({onlySelf: true, emitEvent: false});
        this.netTotalPrice.disable({onlySelf: true, emitEvent: false});
      }
      if (this.priceOptionNet) {
        this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
        this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
      }
    }

    this.getProductUnits();
    this.getProductById(+selectedProduct.id);
  }

  selectVariation(selectedVariation: ProductModel): void {
    this.selectedProduct = this.productsList.find(itm => +itm.id === +selectedVariation.productId);
    this.selectPositionChanges(this.selectedProduct);
  }

  public getProductUnits(): void {
    this.productUnitApiService.getUnits$(
      this.selectedProduct.type || this.selectedProduct.entityForm || this.arbitraryProductTypeControl.value
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        this.unitsListSource = unitsList;
        this.unitsList = unitsList.map(u => u.name);
      });
  }

  // get selectedGLProductType(): any {
  //   if (!this.generalProductsList$.getValue().length || !this.selectedProduct) { return null; }
  //
  //   return this.generalProductsList$.getValue().find(generalProduct => generalProduct.id === this.selectedProduct.id);
  // }

  public getProductById(productId) {
    this.productsService.getProduct(productId, this.data.salesPriceListName)
      .pipe(takeUntil(this._destroy))
      .subscribe(product => {
        this.initForm(product);
        this.setBundledProducts(product);
        if (this.data.vatBlocked) {
          const defaultPrice = this.setCurrencyValue(get(product, 'prices.price.netPrice'));
          this.vat.setValue(0);
          this.grossUnitPrice.setValue(defaultPrice);
          this.grossTotalPrice.setValue(defaultPrice);
        }

        // if (product.productType === ProductTypes.BUNDLE && get(product, 'prices.price.priceFromBundledProducts')) {
        //   this.netTotalPrice.disable({onlySelf: true, emitEvent: false});
        //   this.netUnitPrice.disable({onlySelf: true, emitEvent: false});
        //   this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
        //   this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
        // }

        this.relatedProducts = product.outgoingRelatedProducts.map(itm => ({
          ...itm,
          enabled: itm.necessarily
        }));

        this.marginTotal = null;
        this.marginPercent = null;
        this.updatePosition(1, 'quantity');
    });
  }

  public setBundledProducts(product: ProductModel): void {
    this.bundledProducts = [];
    if (product.bundledProducts) {
      this.bundledProducts = product.bundledProducts;
    }
  }

  public resetBundledProducts(): void {
    this.bundledProducts = [];
    this.activeTab = 'OVERVIEW';
  }

  private getProducts(): void {
    this.productsService.getActiveProducts(undefined, undefined, true, this.data.salesPriceListName)
      .pipe(takeUntil(this._destroy))
      .subscribe((products: ProductModel[]) => {
        this.productsList = this.prepareProductsList(products);
        this.showDropdownSpin$.next(false);
      });
  }

  public prepareProductsList(products): ProductModel[] {
    return products.map(item => ({
      ...item,
      searchLabel: getProductSearchValue(item, this.translateService)
    }));
  }

  public createProduct(productType: ProductTypes): void {
    const dialog = this.dialog.open(CompositeProductModalComponent, {
      data: {
        family: null,
        productType: productType
      },
      disableClose: true,
    });

    dialog.afterClosed().subscribe((product: ProductModel) => {
      if (product) {
        this.productsList.push(product);
        this.selectedProduct = product;
        this.selectedProductType = product.productType;
        this.selectPositionChanges(product);
        this.setBundledProducts(product);
      }
    });
  }

  public displayUserDefinedPosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.REGULAR);
    this.selectedProductType = ProductTypes.ARBITRARY;
    this.selectedProduct = { name: this.translateService.instant('POSITIONS.USER_DEFINED_POSITION') };
    this.productTypeControl.setValue(ProductTypes.ARBITRARY);
    this.positionName = null;
    this.initForm();
    this.getProductUnits();
    if (this.data.vatBlocked) {
      this.vat.disable({onlySelf: true, emitEvent: false});
      this.vat.setValue(0);
      if (this.priceOptionGross) {
        this.netUnitPrice.disable({onlySelf: true, emitEvent: false});
        this.netTotalPrice.disable({onlySelf: true, emitEvent: false});
      }
      if (this.priceOptionNet) {
        this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
        this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
      }
    }
  }

  public displayTextPosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.TEXT);
    this.selectedProduct = {
      name: this.translateService.instant('ECOMMERCE.TEXT'),
      icon: 'user-defined'
    } as any;
  }

  public displayImagePosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.IMAGE);
    this.selectedProduct = {
      name: this.translateService.instant('FORM.IMAGE'),
      icon: 'image'
    } as any;
  }

  public addOfferPosition() {
    if (this.addPositionLoadingRequest$.getValue()) { return; }
    this.addPositionLoadingRequest$.next(true);

    if (this.positionMediaFormComponentRef) {
      if (this.positionMediaFormComponentRef.mediaType === PositionMediaTypeEnum.TEXT) {
        this.addMediaPosition(PositionMediaTypeEnum.TEXT, {description: this.positionMediaFormComponentRef.textValue});
        return;
      }
      if (this.positionMediaFormComponentRef.mediaType === PositionMediaTypeEnum.IMAGE) {
        this.addMediaPosition(
          PositionMediaTypeEnum.IMAGE,
          {
            file: this.positionMediaFormComponentRef.images && this.positionMediaFormComponentRef.images[0],
            height: this.positionMediaFormComponentRef.heightControlValue as any,
            width: this.positionMediaFormComponentRef.widthControlValue as any,
            alignment: this.positionMediaFormComponentRef.alignmentControlValue as any,
            useAspectRatio: this.positionMediaFormComponentRef.useAspectRatioControlValue as any,
          }
        );
        return;
      }
    }

    this.addRegularPosition();
  }

  public addMediaPosition(
    mediaType: PositionMediaTypeEnum,
    data: {
      file?: File,
      description?: string,
      width?: number,
      height?: number,
      alignment?: PdfSettingsAlignEnum,
      useAspectRatio?: boolean,
    }
  ): void {
    data['groupId'] = this.data.groupId;
    this.tradeOfferApiService.createPositionMedia(this.data.tradeOffer.id, mediaType, data)
      .pipe(finalize(() => this.addPositionLoadingRequest$.next(false)))
      .subscribe((response: PositionGroupModel[]) => this.dialogRef.close(response));
  }

  public displayCreditPosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.REGULAR);
    this.selectedProductType = ProductTypes.CREDIT;
    this.productTypeControl.setValue(ProductTypes.CREDIT);
    this.resetPricesControls();
    this.selectedProduct = { name: this.translateService.instant('Credit position') };
    if (this.data.vatBlocked) {
      this.vat.setValue(0);
    } else {
      this.vat.setValue(null);
    }
  }

  public resetPricesControls(): void {
    this.netUnitPrice.patchValue(null);
    this.netTotalPrice.patchValue(null);
    this.grossUnitPrice.patchValue(null);
    this.grossTotalPrice.patchValue(null);
  }

  public addRegularPosition(): void {
    if ((this.typeArbitrary || this.typeCredit) && !this.positionName) {
      this.productNameInvalid = true;
      this.addPositionLoadingRequest$.next(false);
      return;
    }

    if (!this.selectedProduct) {
      this.selectedProductInvalid = true;
      this.addPositionLoadingRequest$.next(false);
      return;
    }

    this.tradeOfferApiService
      .createTradeOfferPosition(
        this.data.tradeOfferId,
        {
          ...this.form.getRawValue(),
          name,
          positionType: this.typeCredit
            ? ProductTypes.CREDIT
            : null,
          positionName: this.typeCredit || this.typeArbitrary
            ? this.positionName
            : this.selectedProduct.name,
          grossTotalPrice: this.setCurrencyControl(this.grossTotalPrice.value),
          grossUnitPrice: this.setCurrencyControl(this.grossUnitPrice.value),
          netTotalPrice: this.setCurrencyControl(this.netTotalPrice.value),
          netUnitPrice: this.setCurrencyControl(this.netUnitPrice.value),
          productId: this.selectedProduct.id,
          productType: this.selectedProductType,
          productDescription: !this.typeServices && !this.isGeneralServiceType && !this.typeArbitrary
            ? this.productDescription.value
            : null,
          providedServices: this.typeServices || this.isGeneralServiceType || this.typeArbitrary
            ? this.providedServices.value
            : null,
          productRelatedIds: this.relatedProducts
            ? this.relatedProducts
                .filter(itm => itm.enabled)
                .map(itm => itm.id)
            : [],
          groupId: this.typeCredit
            ? null
            : this.data.groupId
        })
      .pipe(
        finalize(() => this.addPositionLoadingRequest$.next(false)),
        takeUntil(this._destroy))
      .subscribe(this.subscribeHandler);
  }

  public setCurrencyControl(val): number {
    if (!val) { return val; }
    return +(val * 100).toFixed(0);
  }

  public subscribeHandler = (response: ResponseModel<TradeOfferPositionsByProductType>) => {
    this.tradeOfferApiService.getTradeOfferById(this.data.tradeOfferId).subscribe();
    this.dialogRef.close(response);
  }

  public updatePosition(fieldValue, fieldName): void {
    this.addPositionLoadingRequest$.next(true);

    this.recalculatePositionApiService.recalculatePosition(DocumentTypesUppercaseEnum.OFR, {
      protoPosition: {
        ...this.form.getRawValue(),
        salesPriceListName: this.data.salesPriceListName,
        productId: this.selectedProduct.id,
        productTypeForGrouping: this.selectedProductType,
        arbitraryPositionProductType: this.arbitraryProductTypeControl.value,
        productDescription: !this.typeServices && !this.isGeneralServiceType && !this.typeArbitrary
          ? this.productDescription.value
          : null,
        providedServices: this.typeServices || this.isGeneralServiceType || this.typeArbitrary
          ? this.providedServices.value
          : null,
        priceFromBundledProducts: get(this.selectedProduct, 'price_from_bundled_products')
          || get(this.selectedProduct, 'prices.price.priceFromBundledProducts'),
        bundleType: get(this.selectedProduct, 'bundle_type')
          || get(this.selectedProduct, 'bundleType'),
        priceOption: this.data.tradeOffer.preferences.priceOption,
      },
      fieldUpdateRequest: {
        fieldName,
        fieldValue
      },
      type: this.selectedProductType
    })
      .pipe(
        finalize(() => this.addPositionLoadingRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe(product => {
        this.form.patchValue(product);
        this.previousUnitType = product.unitType;
        this.previousUpdatedPosition = product;
        this.selectedProduct = {
          ...this.selectedProduct,
          description: this.selectedProductType === ProductTypes.SERVICES ? product.providedServices : product.productDescription
        };

        this.marginPercent = product.marginPercent;
        this.marginTotal = product.marginTotal;
      }, (error) => {
        if (error && error.error && error.error.errorCodes) {
          if (error.error.errorCodes.length &&  error.error.errorCodes.includes(1786)) {
            if (this.previousUpdatedPosition) {
              this.grossTotalPrice.patchValue(this.previousUpdatedPosition.grossTotalPrice);
              this.grossUnitPrice.patchValue(this.previousUpdatedPosition.grossUnitPrice);
              this.netTotalPrice.patchValue(this.previousUpdatedPosition.netTotalPrice);
              this.netUnitPrice.patchValue(this.previousUpdatedPosition.netUnitPrice);
            }
          }
        }
        if (fieldName === 'unitType') {
          // set previous value of unit type in case of error
          this.unitTypeControl.patchValue(this.previousUnitType);
          // redraw quantity control view
          setTimeout(() => this.quantity.patchValue(this.quantity.value), 10);
        }
      });
  }

  public updateDescription(description: string): void {
    this.selectedProduct = {
      ...this.selectedProduct,
      description: description
    };
  }

  public setCurrencyValue(value): number {
    if (!value) { return value; }
    return +(value / 1e2).toFixed(2);
  }

  public updateDescriptionByProductType(): void {
   if (this.typeServices || this.isGeneralServiceType || this.typeArbitrary) {
       this.updatePosition(this.providedServices.value, 'providedServices');
   } else {
     this.updatePosition(this.productDescription.value, 'productDescription');
   }
  }

  get isGeneralServiceType(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.entityForm === ProductTypes.SERVICES;
  }

  get typeGeneral(): boolean {
    return this.selectedProductType === ProductTypes.GENERAL;
  }
  get typeCredit(): boolean {
    return this.selectedProductType === ProductTypes.CREDIT;
  }
  get typeServices(): boolean {
    return this.selectedProductType === ProductTypes.SERVICES;
  }
  get typeGoods(): boolean {
    return this.selectedProductType === ProductTypes.GOODS;
  }
  get typeArbitrary(): boolean {
    return this.selectedProductType === ProductTypes.ARBITRARY;
  }

  get allowFractionalValues(): boolean {
    if (!this.unitTypeControl.value || !this.unitsListSource.length) { return true; }
    const selectedUnit = this.unitsListSource
      .find((itm: ProductUnitModel) => itm.name === this.unitTypeControl.value);
    return selectedUnit
      ? selectedUnit.allowFractionalValues
      : false;
  }

  get priceOptionNet(): boolean {
    return this.data.tradeOffer.preferences.priceOption === PriceOptionEnum.NET
      || this.data.tradeOffer.properties.smallBusiness;
  }
  get priceOptionGross(): boolean {
    return this.data.tradeOffer.preferences.priceOption === PriceOptionEnum.GROSS
      && !this.data.tradeOffer.properties.smallBusiness;
  }

  get mediaType(): PositionMediaTypeEnum {
    if (this.positionMediaFormComponentRef && this.positionMediaFormComponentRef.mediaType) {
      return this.positionMediaFormComponentRef.mediaType;
    }
    return PositionMediaTypeEnum.REGULAR;
  }

  get grossTotalPrice(): FormControl { return this.form.get('grossTotalPrice') as FormControl; }
  get grossUnitPrice(): FormControl { return this.form.get('grossUnitPrice') as FormControl; }
  get addRemark(): FormControl { return this.form.get('addRemark') as FormControl; }
  get netTotalPrice(): FormControl { return this.form.get('netTotalPrice') as FormControl; }
  get netUnitPrice(): FormControl { return this.form.get('netUnitPrice') as FormControl; }
  get productDescription(): FormControl { return this.form.get('productDescription') as FormControl; }
  get providedServices(): FormControl { return this.form.get('providedServices') as FormControl; }
  get quantity(): FormControl { return this.form.get('quantity') as FormControl; }
  get unitTypeControl(): FormControl { return this.form.get('unitType') as FormControl; }
  get remark(): FormControl { return this.form.get('remark') as FormControl; }
  get vat(): FormControl { return this.form.get('vat') as FormControl; }
  get discount(): FormControl { return this.form.get('discount') as FormControl; }
  get discountAmountNet(): FormControl { return this.form.get('discountAmountNet') as FormControl; }
  get discountAmountGross(): FormControl { return this.form.get('discountAmountGross') as FormControl; }
  get addDiscount(): FormControl { return this.form.get('addDiscount') as FormControl; }
  get productTypeControl(): FormControl { return this.form.get('productType') as FormControl; }
  get arbitraryProductTypeControl(): FormControl { return this.form.get('arbitraryProductType') as FormControl; }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
